import { Card, ThemeProvider } from "@mui/material";
import LoadingScreen from "components/loading/LoadingScreen";
import Table from "components/table/Table";
import {
  ALL_OPTION,
  BUSINESS_PHONE_NUMBER_TYPES,
  BUSINESS_TELECOM_OPERATORS,
} from "constant";
import { useFormik } from "formik";
import { usePagination } from "hooks/usePagination";
import { useMemo, useState } from "react";
import {
  useExportListBusinessPhoneNumber,
  useGetListBusinessPhoneNumber,
  useGetListPbxType,
} from "service/query/business-management.query";
import muiTheme from "theme/muiTheme";

import BusinessPhoneNumberFilter from "./components/BusinessPhoneNumberFilter";

const BusinessPhoneNumber = ({ location }) => {
  const DEFAULT_FORM_VALUE = {
    pbxType: ALL_OPTION.value, // loại tổng đài
    typeDial: ALL_OPTION.value, // loại đầu số
    businessCode: location.businessCode ?? ALL_OPTION.value,
    telecomOperator: ALL_OPTION.value, // nhà mạng
    phoneNumber: "",
  };

  const { data: listPbxType } = useGetListPbxType();

  const [filter, setFilter] = useState(
    convertFormValueToFilter(DEFAULT_FORM_VALUE)
  );

  const { data: listBusinessPhoneNumber, isFetching } =
    useGetListBusinessPhoneNumber();

  const { refetch: handleExportListBusinessPhoneNumber } =
    useExportListBusinessPhoneNumber(filter);

  const filteredData = useMemo(
    () =>
      filter
        ? listBusinessPhoneNumber?.filter(
            (item) =>
              (filter.businessCode
                ? item.businessCode === filter.businessCode
                : true) &&
              (filter.pbxType ? item.pbxType === filter.pbxType : true) &&
              (filter.typeDial ? item.typeDial === filter.typeDial : true) &&
              (filter.phoneNumber
                ? item.phoneNumber
                    ?.toLowerCase()
                    .includes(filter.phoneNumber.trim().toLowerCase())
                : true) &&
              (filter.telecomOperator
                ? item.telecomOperator === filter.telecomOperator
                : true)
          )
        : listBusinessPhoneNumber,
    [listBusinessPhoneNumber, filter]
  );

  const {
    page,
    pageSize,
    handleChangePage,
    handleChangePageSize,
    clientData,
    clientTotal,
  } = usePagination(filteredData);

  const handleSearchListBusinessPhoneNumber = (formValue) => {
    setFilter(convertFormValueToFilter(formValue));
    handleChangePage(1);
  };

  const formik = useFormik({
    initialValues: DEFAULT_FORM_VALUE,
    onSubmit: handleSearchListBusinessPhoneNumber,
  });

  const columns = useMemo(
    () => [
      {
        id: "pbxType",
        name: "Loại tổng đài",
        render: (record) =>
          record.pbxType
            ? listPbxType?.find((item) => item.pbxType === record.pbxType)?.name
            : null,
      },
      {
        id: "businessName",
        name: "Tên nghiệp vụ",
      },
      {
        id: "typeDial",
        name: "Loại đầu số",
        render: (record) =>
          record.typeDial
            ? BUSINESS_PHONE_NUMBER_TYPES.find(
                (item) => item.value === record.typeDial
              )?.label
            : null,
      },
      {
        id: "telecomOperator",
        name: "Nhà mạng",
        render: (record) =>
          record.telecomOperator
            ? BUSINESS_TELECOM_OPERATORS.find(
                (item) => item.value === record.telecomOperator
              )?.label
            : null,
      },
      {
        id: "phoneNumber",
        name: "Đầu số",
      },
      { id: "updatedAt", name: "Ngày cập nhật", type: "datetime" },
    ],
    [listPbxType]
  );

  return (
    <ThemeProvider theme={muiTheme}>
      <Card sx={{ p: 2 }}>
        <BusinessPhoneNumberFilter
          formik={formik}
          onClickDownload={handleExportListBusinessPhoneNumber}
        />
      </Card>

      <Card sx={{ mt: 2 }}>
        <Table
          columns={columns}
          data={clientData}
          toolbar={{
            title: "Danh sách đầu số nghiệp vụ",
          }}
          pagination={{
            page,
            pageSize,
            totalItems: clientTotal,
            onPageChange: handleChangePage,
            onPageSizeChange: handleChangePageSize,
          }}
        />
      </Card>

      <LoadingScreen loading={isFetching} />
    </ThemeProvider>
  );
};

const convertFormValueToFilter = ({
  businessCode,
  phoneNumber,
  telecomOperator,
  pbxType,
  typeDial,
}) => ({
  ...(businessCode !== ALL_OPTION.value && {
    businessCode,
  }),
  ...(pbxType !== ALL_OPTION.value && {
    pbxType,
  }),
  ...(typeDial !== ALL_OPTION.value && {
    typeDial,
  }),
  ...(telecomOperator !== ALL_OPTION.value && {
    telecomOperator,
  }),
  ...(phoneNumber && { phoneNumber }),
});

export default BusinessPhoneNumber;
